<script setup lang="ts">
import { useRouter } from 'vue-router';
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'reka-ui';
import { Bell, Bot, Gauge, LogOut, MonitorSmartphone, Palette, ShieldCheck } from 'lucide-vue-next';
import DAvatar from '../../design/components/DAvatar.vue';
import { useSessionStore } from '../../app/stores/session.ts';
import { useTheme } from '../../app/composables/useTheme.ts';
import { useSignOut } from '../../app/composables/useSignOut.ts';

withDefaults(defineProps<{ side?: 'top' | 'bottom' | 'right' }>(), { side: 'bottom' });

const session = useSessionStore();
const router = useRouter();
const { theme, cycle } = useTheme();
const THEME_LABELS = { system: 'System', light: 'Light', dark: 'Dark' } as const;

const signOutWith = useSignOut();
const signOut = (everywhere: boolean) => signOutWith({ everywhere });
</script>

<template>
  <DropdownMenuRoot v-if="session.user">
    <DropdownMenuTrigger class="avatar-button" :aria-label="`Account: ${session.user.name}`">
      <DAvatar :name="session.user.name" :seed="session.user.id" />
    </DropdownMenuTrigger>
    <DropdownMenuPortal>
      <DropdownMenuContent
        class="menu"
        :side="side"
        align="end"
        :side-offset="6"
        :collision-padding="8"
      >
        <DropdownMenuLabel class="menu__label">
          <div class="truncate">{{ session.user.name }}</div>
          <div class="muted truncate">{{ session.user.email }}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator class="menu__separator" />
        <DropdownMenuItem class="menu__item" @select="router.push({ name: 'plan' })">
          <Gauge :size="15" :stroke-width="1.75" aria-hidden="true" />
          Your plan
        </DropdownMenuItem>
        <DropdownMenuItem
          class="menu__item"
          @select="router.push({ name: 'notification-settings' })"
        >
          <Bell :size="15" :stroke-width="1.75" aria-hidden="true" />
          Notification settings
        </DropdownMenuItem>
        <DropdownMenuItem class="menu__item" @select="router.push({ name: 'security-settings' })">
          <ShieldCheck :size="15" :stroke-width="1.75" aria-hidden="true" />
          Security
        </DropdownMenuItem>
        <DropdownMenuItem class="menu__item" @select="router.push({ name: 'my-agents' })">
          <Bot :size="15" :stroke-width="1.75" aria-hidden="true" />
          My agents
        </DropdownMenuItem>
        <DropdownMenuItem class="menu__item" @select.prevent="cycle">
          <Palette :size="15" :stroke-width="1.75" aria-hidden="true" />
          <span class="spacer">Theme</span>
          <span class="muted">{{ THEME_LABELS[theme] }}</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator class="menu__separator" />
        <DropdownMenuItem class="menu__item" @select="signOut(false)">
          <LogOut :size="15" :stroke-width="1.75" aria-hidden="true" />
          Log out
        </DropdownMenuItem>
        <DropdownMenuItem class="menu__item menu__item--danger" @select="signOut(true)">
          <MonitorSmartphone :size="15" :stroke-width="1.75" aria-hidden="true" />
          Log out of all devices
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
</template>
